import { FC, MouseEventHandler, useState, useEffect } from 'react';

import dynamic from 'next/dynamic';

import { AnalyticsContext, Button as IButton, Maybe } from '@/types/generated';
import {
	resolveContentfulButtonSize,
	resolveContentfulButtonVariant,
	resolveContentfulResponsiveButton,
	ButtonIconSizes,
} from '@/utils';
import { ContentfulButtonSize, ContentfulButtonVariant } from '@/types';
import TAGSvgIcon, { IconName } from '@/components/TAGSvgIcon';
import TAGButton, { type ITAGButton } from '@/components/TAGButton';
import { useAppContext, useFacilityContext, useOfficeLocationContext } from '@/context';
import { resolveEntryLink } from '@/utils/resolveButtonEntryLink/resolveButtonEntryLink';
import TeamTagDialog from '@/snowflakes/teamtag/TeamTagDialog';
import { replaceContentVars } from '@/utils/replaceContentVars/replaceContentVars';
import {
	hasReachedVirtualTryOnSubmissionLimit,
	VIRTUAL_TRY_ON_COUNT_CHANGE_EVENT,
} from '@/utils/virtualTryOnSubmissionCounter';

import Quiz from '../Quiz';
import { TFormData, quizGA4DataLayerPush } from '../Quiz/Quiz.helpers';
import { DynamicFormConfig } from '../DynamicForm/types/DynamicFormTypes';

import { buttonStyles } from './ContentfulButton.styles';
import { triggerCrestaChat, useCrestaAgent } from './useCrestaAgent';

const DentrinoVirtualTryOn = dynamic(() => import('../DentrinoVirtualTryOn/DentrinoVirtualTryOn'));

interface IContentfulButton extends IButton {
	isSmallScreen?: boolean;
	dataTestId?: string;
	defaultVariant?: ITAGButton['variant'];
	onClick?: MouseEventHandler<HTMLButtonElement>;
	fullWidth?: boolean;
	analyticsContext?: Maybe<AnalyticsContext>;
	ariaLabel?: string;
	docProps?: Record<string, unknown>;
}

const openLinkIn = (link: string, typeOfOpening: 'same_tab' | 'new_tab' | 'new_window') => {
	if (typeOfOpening === 'new_tab') {
		window.open(link, '_blank');
	} else if (typeOfOpening === 'new_window') {
		window.open(link, '_blank', 'width=600,height=400');
	} else {
		window.location.href = link;
	}
};

const getTypeOfOpening = (
	openInNewWindow?: boolean | null,
	openInNewTab?: boolean | null
): 'new_window' | 'new_tab' | 'same_tab' => {
	if (openInNewWindow) return 'new_window';
	if (openInNewTab) return 'new_tab';
	return 'same_tab';
};

export const buttonIconSize = (size?: Maybe<ButtonIconSizes>): number => {
	const sizes = {
		XSmall: 12,
		Small: 16,
		Medium: 25,
		Large: 32,
		XLarge: 38,
	} as { [key: string]: number };
	return sizes[size || 'Medium'];
};

const ContentfulButton: FC<IContentfulButton> = ({
	defaultVariant,
	dataTestId = 'tag_button',
	isSmallScreen = false,
	onClick = () => undefined,
	fullWidth = false,
	quizDetails,
	ariaLabel,
	docProps = {},
	popoverContentCollection,
	launchCrestaAgent,
	...buttonData
}) => {
	const [state, setState] = useState<{
		showQuiz: boolean;
		showDentrinoVirtualTryOn: boolean;
	}>({
		showQuiz: false,
		showDentrinoVirtualTryOn: false,
	});
	const [open, setOpen] = useState(false);
	const [isVirtualTryOnLimitReached, setIsVirtualTryOnLimitReached] = useState(
		hasReachedVirtualTryOnSubmissionLimit()
	);
	const { ready: crestaReady } = useCrestaAgent(!!launchCrestaAgent);

	// Update disabled state when submission count changes
	useEffect(() => {
		const checkLimit = () => {
			setIsVirtualTryOnLimitReached(hasReachedVirtualTryOnSubmissionLimit());
		};

		checkLimit();

		globalThis.addEventListener(VIRTUAL_TRY_ON_COUNT_CHANGE_EVENT, checkLimit);

		globalThis.addEventListener('storage', checkLimit);

		return () => {
			globalThis.removeEventListener(VIRTUAL_TRY_ON_COUNT_CHANGE_EVENT, checkLimit);
			globalThis.removeEventListener('storage', checkLimit);
		};
	}, []);
	const { config, stateCodeFromHeaders } = useAppContext();
	const { officeInfo } = useFacilityContext();
	const { officeInfo: officeLocationInfo } = useOfficeLocationContext();
	if (!buttonData) return null;
	const button = resolveContentfulResponsiveButton(buttonData, isSmallScreen);
	const resolvedLink = resolveEntryLink({
		path: button?.link,
		typeName: buttonData?.navigationDetails?.pageData?.__typename as string,
		config,
		officeInfo,
		officeLocationInfo,
	});
	const typeOfOpening = getTypeOfOpening(button.openInNewWindow, button.openInNewTab);

	const handleRenderQuiz = (quiz: string) => {
		switch (quiz) {
			case 'ToothReplacement':
				setState({
					...state,
					showQuiz: true,
				});

				if (!quizDetails?.isFormBuilder) {
					quizGA4DataLayerPush(
						config.name,
						'start',
						'N/A',
						quizDetails?.quizFormData as TFormData,
						stateCodeFromHeaders
					);
				}
				break;
			case 'DentrinoVirtualTryOn':
			case 'DentrinoVirtualTryOnNoScheduling':
				setState({
					...state,
					showDentrinoVirtualTryOn: true,
				});
				break;
			default:
				return null;
		}
	};

	const handleCloseQuiz = () => {
		setState({
			...state,
			showQuiz: false,
			showDentrinoVirtualTryOn: false,
		});
	};

	const handleButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		if (launchCrestaAgent) {
			e.preventDefault();
			triggerCrestaChat();
			return;
		}
		if ((popoverContentCollection?.items ?? []).length > 0) {
			e.preventDefault();
			setOpen(true);
			return;
		}
		if (quizDetails?.quizType) {
			handleRenderQuiz(quizDetails.quizType);
			return;
		}
		if (button?.externalUrl) {
			if (typeOfOpening !== 'same_tab') {
				e.preventDefault();
				openLinkIn(button?.externalUrl, typeOfOpening);
			}
			return;
		}
		if (buttonData?.navigationDetails?.isInternal && resolvedLink) {
			if (typeOfOpening !== 'same_tab') {
				e.preventDefault();
				openLinkIn(resolvedLink, typeOfOpening);
			}
			return;
		}
		onClick(e);
	};

	const buttonLabel = replaceContentVars({
		text: button?.label,
		officeInfo,
		officeLocationInfo,
		config,
	});

	return (
		<>
			{state.showQuiz ? (
				<Quiz
					openQuiz={state.showQuiz}
					handleCloseQuiz={handleCloseQuiz}
					quizFormData={quizDetails?.quizFormData as JSON}
					submitEndpointUrl={quizDetails?.submitEndpointUrl ?? ''}
					showInDialogOrDrawer={true}
					quizType={quizDetails?.quizType ?? 'ToothReplacement'}
					isFormBuilder={quizDetails?.isFormBuilder ?? false}
					formBuilderConfig={quizDetails?.formBuilderConfig as DynamicFormConfig}
				/>
			) : null}
			{state.showDentrinoVirtualTryOn ? (
				<DentrinoVirtualTryOn
					openVirtualTry={state.showDentrinoVirtualTryOn}
					handleCloseVirtualTry={handleCloseQuiz}
					quizFormData={quizDetails?.quizFormData as TFormData}
					submitEndpointUrl={quizDetails?.submitEndpointUrl?.trim() || undefined}
					quizType={quizDetails?.quizType || undefined}
				/>
			) : null}
			{(popoverContentCollection?.items ?? []).length > 0 ? (
				<TeamTagDialog
					open={open}
					handleClose={() => setOpen(false)}
					contentCollection={popoverContentCollection}
				/>
			) : null}
			<TAGButton
				href={
					quizDetails?.quizType ||
					(popoverContentCollection?.items ?? []).length > 0 ||
					typeOfOpening !== 'same_tab'
						? undefined
						: button?.externalUrl ?? resolvedLink ?? undefined
				}
				size={resolveContentfulButtonSize(button?.size as ContentfulButtonSize)}
				variant={resolveContentfulButtonVariant(button?.variant as ContentfulButtonVariant, defaultVariant)}
				startIcon={
					button?.iconPosition === 'start' && button?.icon ? (
						<TAGSvgIcon
							icon={button.icon as IconName}
							size={buttonIconSize(button.iconSize || 'Medium')}
							dataTestId={`${dataTestId}_start_icon_${button.id}`}
						/>
					) : null
				}
				endIcon={
					button?.iconPosition === 'end' && button?.icon ? (
						<TAGSvgIcon
							icon={button.icon as IconName}
							size={buttonIconSize(button.iconSize || 'Medium')}
							dataTestId={`${dataTestId}_end_icon_${button.id}`}
						/>
					) : null
				}
				aria-label={ariaLabel}
				analyticsContext={buttonData?.analyticsContext}
				disabled={
					(!!launchCrestaAgent && !crestaReady) ||
					((quizDetails?.quizType === 'DentrinoVirtualTryOn' ||
						quizDetails?.quizType === 'DentrinoVirtualTryOnNoScheduling') &&
						isVirtualTryOnLimitReached)
				}
				onClick={handleButtonClick}
				sx={{
					...buttonStyles,
					...docProps,
					px: button?.icon ? '0.875rem' : '1.5rem',
					width: fullWidth ? '100%' : 'auto',
				}}
				dataTestId={`${dataTestId}_${button?.id ?? 'defaultId'}`}
			>
				{buttonLabel}
			</TAGButton>
		</>
	);
};
export default ContentfulButton;
